"use client";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { RSVPStepsFormInputData } from "../validators";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/v3/shadcn/Tooltip";
import { useParams } from "next/navigation";
import { FaCheck, FaInfoCircle } from "react-icons/fa";
import { GoEyeClosed } from "react-icons/go";
import { LuCopy } from "react-icons/lu";
import { toast } from "react-toastify";
import { If } from "~/components/v3/Conditional";
import { Input } from "~/components/v3/themed/Input";
import { cn } from "~/utils/fontsConfig";
import { getRSVPStepsAndPageData } from "../actions/rsvp-steps-and-data";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "~/components/v3/shadcn/Card";
import { Button } from "~/components/v3/themed/Button";

type Props = {
    setIsNextDisabled: (disabled: boolean) => void;
    eventTicketPackage?: Awaited<
        ReturnType<typeof getRSVPStepsAndPageData>
    >["eventInfo"]["eventTicketPackage"];
    hasManagerPermission?: boolean;
    isMembershipBypassTicket?: boolean;
    selectedCard?: "membership" | "ticket" | undefined;
    setSelectedCard?: (card: "membership" | "ticket" | undefined) => void;
    ticketProductDescription?: string;
    isMembershipPurchaseDisabled?: boolean;
};

export const formatPrice = (
    priceDollars?: number | null,
    priceCents?: number | null,
    isFree?: boolean | null,
) => {
    if (isFree) return "Free";
    const dollars = priceDollars ?? 0;
    const cents = priceCents ?? 0;
    if (dollars === 0 && cents === 0) return "Price not available";
    return `$${dollars}.${cents.toString().padStart(2, "0")}`;
};

const BuyTicketStep = ({
    eventTicketPackage,
    hasManagerPermission,
    setIsNextDisabled,
    isMembershipBypassTicket,
    selectedCard,
    setSelectedCard,
    ticketProductDescription,
    isMembershipPurchaseDisabled,
}: Props) => {
    const form = useFormContext<RSVPStepsFormInputData>();
    const params = useParams();
    const eventSlug = params?.slug;

    useEffect(() => {
        const isFormValid = form.watch("ticket.selectedTierId") !== undefined;
        setIsNextDisabled(!isFormValid);
    }, [form.watch("ticket.selectedTierId")]);

    if (!eventTicketPackage || eventTicketPackage?.ticketTiers?.length === 0) {
        throw Error(
            "No event ticket package. Most likely an error in the steps builder.",
        );
    }

    const hasDiscountCodes =
        eventTicketPackage.discountCodes &&
        eventTicketPackage.discountCodes?.length > 0;

    const selectedTierId = form.watch("ticket.selectedTierId");

    const handleTicketSelection = (tierId: string) => {
        form.setValue("ticket.selectedTierId", tierId, { shouldDirty: true });
        form.setValue("selectedMembership", undefined);
        form.setValue("payOnce", true);
        setSelectedCard && setSelectedCard("ticket");
        setIsNextDisabled(false);
    };

    if (isMembershipBypassTicket) {
        const selectedTicketTier = eventTicketPackage?.ticketTiers?.[0];
        if (!selectedTicketTier) {
            throw new Error("No ticket tier found");
        }

        const formattedPrice = formatPrice(
            selectedTicketTier.priceDollars,
            selectedTicketTier.priceCents,
            selectedTicketTier.isFree,
        );

        return (
            <Card
                className={cn(
                    "flex h-full w-full flex-col p-4 ",
                    selectedCard === "ticket" && "border-2 border-v3-P75",
                    isMembershipPurchaseDisabled ? "" : "md:h-[300px] md:w-1/2",
                )}
            >
                <div className="flex h-full w-full flex-col">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 pb-2 text-v3-P75">
                            Single ticket
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="cursor-help">
                                            <FaInfoCircle size={12} />
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        This lets you attend the event without
                                        having to purchase a membership!
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </CardTitle>
                        <div className="flex items-center gap-2">
                            <If condition={!selectedTicketTier.isPublic}>
                                <GoEyeClosed className="text-v3-primary" />
                                <If condition={!!hasManagerPermission}>
                                    <LuCopy
                                        onClick={() => {
                                            navigator.clipboard
                                                .writeText(
                                                    `https://app.getriver.io/events/${eventSlug}/ticket/${selectedTicketTier.id}`,
                                                )
                                                .then(() =>
                                                    toast.success(
                                                        "Copied secret URL to clipboard",
                                                    ),
                                                )
                                                .catch(() =>
                                                    toast.error(
                                                        "Failed to copy secret URL",
                                                    ),
                                                );
                                        }}
                                    />
                                </If>
                            </If>
                        </div>
                        <CardTitle className="text-white">
                            {formattedPrice}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-grow">
                        <CardDescription className="gap-2 text-sm text-gray-400">
                            <div>One time purchase</div>
                            <div className="mt-4">
                                {ticketProductDescription}
                            </div>
                        </CardDescription>

                        {(selectedTicketTier.usageLimit ||
                            selectedTicketTier.usedTimes > 0) && (
                            <p className="mt-2 text-sm text-gray-400">
                                {selectedTicketTier.usedTimes ?? 0} /{" "}
                                {selectedTicketTier.usageLimit ?? "∞"}
                            </p>
                        )}
                    </CardContent>
                    <CardFooter className="mt-auto">
                        <Button
                            className="w-full"
                            variant={
                                selectedCard === "ticket"
                                    ? "default"
                                    : "secondary"
                            }
                            onClick={() =>
                                handleTicketSelection(selectedTicketTier.id)
                            }
                        >
                            {selectedCard === "ticket" ? "Selected" : "Select"}
                        </Button>
                    </CardFooter>
                </div>
            </Card>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p className="mb-4">Welcome! Please choose your desired ticket:</p>
            <div className="flex w-full flex-col items-center gap-2">
                {eventTicketPackage.ticketTiers?.map((tier) => (
                    <Card
                        key={tier.id}
                        className={cn(
                            "flex h-full w-full flex-col p-4 md:h-[300px] md:w-1/2",
                            tier.id === selectedTierId &&
                                "border-2 border-v3-P75",
                        )}
                    >
                        <div className="flex h-full w-full flex-col">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 pb-2 text-v3-P75">
                                    Main ticket
                                </CardTitle>
                                <div className="flex items-center gap-2">
                                    <If condition={!tier.isPublic}>
                                        <If condition={!!hasManagerPermission}>
                                            <LuCopy
                                                onClick={() => {
                                                    navigator.clipboard
                                                        .writeText(
                                                            `https://app.getriver.io/events/${eventSlug}/ticket/${tier.id}`,
                                                        )
                                                        .then(() =>
                                                            toast.success(
                                                                "Copied secret URL to clipboard",
                                                            ),
                                                        )
                                                        .catch(() =>
                                                            toast.error(
                                                                "Failed to copy secret URL",
                                                            ),
                                                        );
                                                }}
                                            />
                                        </If>
                                    </If>
                                </div>
                                <CardTitle className="text-white">
                                    {formatPrice(
                                        tier.priceDollars,
                                        tier.priceCents,
                                        tier.isFree,
                                    )}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="flex-grow">
                                <CardDescription className="gap-2 text-sm text-gray-400">
                                    <div>One time purchase</div>
                                    <div className="mt-4">
                                        {ticketProductDescription}
                                    </div>
                                </CardDescription>

                                {(tier.usageLimit || tier.usedTimes > 0) && (
                                    <p className="mt-2 text-sm text-gray-400">
                                        {tier.usedTimes ?? 0} /{" "}
                                        {tier.usageLimit ?? "∞"}
                                    </p>
                                )}
                            </CardContent>
                            <CardFooter className="mt-auto">
                                <Button
                                    className="w-full"
                                    variant={
                                        tier.id === selectedTierId
                                            ? "default"
                                            : "secondary"
                                    }
                                    onClick={() =>
                                        handleTicketSelection(tier.id)
                                    }
                                >
                                    {tier.id === selectedTierId
                                        ? "Selected"
                                        : "Select"}
                                </Button>
                            </CardFooter>
                        </div>
                    </Card>
                ))}
            </div>
            <If condition={!!hasDiscountCodes}>
                <div className="mt-4 flex items-center gap-2 text-gray-400">
                    <div className="grow"></div>
                    Discount code:
                    <Input
                        placeholder="DISCOUNT-ABC"
                        className="w-36 bg-transparent"
                        onChange={(e) =>
                            form.setValue(
                                "ticket.discountCode",
                                e.target.value,
                                {
                                    shouldDirty: true,
                                },
                            )
                        }
                    />
                </div>
            </If>
        </div>
    );
};

export default BuyTicketStep;
